import { inject, Injectable } from '@angular/core';
import { PATH_ITERATION_SYMBOL, PATH_PAGE_ITERATION_SYMBOL, StatefulValue } from './stateful-value.class';
import { CustomTextHelper } from '@workflow/helper/custom-text-helper/custom-text-helper.class';
import { PageHelper } from './page-helper.class';
import { PageRouteHelper } from './page-route-helper.class';
import { ValidationBase } from './validation-base.class';
import { dependencies } from './workflow.dependency';
import { WorkflowPage, CoreCustomPageType, PageType, PrimitiveType } from './workflow.type';
import { CardInstance, CardService } from './card.service';
import { isIterativePage } from '@workflow/helper/page-utility/page.utility';

@Injectable({
  providedIn: 'root'
})
export class PageService {
  private instances: { [id: string]: PageInstance } = {};
  private readonly cardService = inject(CardService);

  public createPage(id: string, flowId: string, pageIterationId?: number): PageInstance {
    const originalId = id;
    if (pageIterationId != null) {
      if (originalId.indexOf(PATH_PAGE_ITERATION_SYMBOL) < 0) {
        throw new Error(`Page iteration ID provided for non-iterative field ID: ${originalId} : ${pageIterationId}`);
      }
      id = id.replace(PATH_PAGE_ITERATION_SYMBOL, String(pageIterationId));
    } else if (originalId.indexOf(PATH_PAGE_ITERATION_SYMBOL) > -1) {
      throw new Error(`Iterative page requires page iteration ID: ${originalId}`);
    }

    const instanceId = `${flowId}/${id}`;
    if (this.instances[instanceId]) {
      return this.instances[instanceId];
    }

    const newInstance = new PageInstance(this.cardService, id, originalId, flowId, pageIterationId);
    this.instances[instanceId] = newInstance;
    return newInstance;
  }

  /**
   * Below method should be called to clear Page
   * Make sure this method called only once
   */
  public clear(): void {
    for (const [id] of Object.entries(this.instances)) {
      delete this.instances[id];
    }
  }

  /**
   * Test configuration values for validity + set sane defaults (e.g. adding implicit IDs, etc)
   * Throws errors on integration fails
   * @param id page ID from `workflow-pages.json`
   */
  public test(id: string): void {
    const config = dependencies.config.pages[id];
    if (this.isPageType(config.type)) {
      // cards syntax and integrity with `workflow-cards.json`
      if (!Array.isArray(config.cards)) {
        throw new Error(`The "cards" property is missing or not an array on page ${id}: ${JSON.stringify(config.cards)}`);
      }

      for (const cardId of config.cards) {
        if (typeof cardId !== 'string' || !cardId) {
          throw new Error(`A cardId is invalid on page ${id}: ${JSON.stringify(cardId)}`);
        }
        if (!dependencies.config.cards[cardId]) {
          throw new Error(`A cardId referenced on page ${id} does not exist in workflow-cards: "${cardId}"`);
        }
      }
    } else if (!this.isPageType(config.type)) {
      this.handlePageType(config, id);
    }

    // TODO: implement test config for requiredPages, avoid circular dependency
  }

  public isPageType(pageType: PageType) {
    return pageType === undefined || pageType === 'page';
  }

  // Moved for SQ Complexity
  public handlePageType(config, id) {
    config.cards = [];

    if (this.isRedirect(config.type) && !config.redirectUrl) {
      throw new Error(`The "redirectUrl" property is missing on page ${id}, because this is a redirect page`);
    }

    if (this.isAction(config.type) && !config.actionLogic) {
      throw new Error(`The "actionLogic" property is missing on page ${id}, because this is a action page`);
    }
  }

  // TODO: Seems overly engineered to have a method to check a string.
  public isRedirect(pageType: PageType) {
    return pageType === 'redirect';
  }

  public isAction(pageType: PageType) {
    return pageType === 'action';
  }

  public ensureConfig(configId: string): void {
    const config = dependencies.config.pages[configId];
    if (!config) {
      return;
    }

    // ensure consistency with IDs
    config.id = configId;

    // implicit path
    config.path = config.path || configId;
  }

  /**
   * Dynamically instantiate page object with iterative support
   * if the page is iterative if will create a list of pages of the same instance
   */
  public build(pageId, flowId, workflowPage): PageInstance[] {
    if (!isIterativePage(workflowPage)) {
      return [this.createPage(pageId, flowId)];
    }

    const currentSize = this.iterationSize(workflowPage);
    if (!currentSize) {
      return [];
    }

    const pages = [];
    for (let i = 0; i < currentSize; i++) {
      pages.push(this.createPage(pageId, flowId, i));
    }
    return pages;
  }

  private iterationSize(workflowPage): number {
    return workflowPage.array && StatefulValue.arraySize(workflowPage.array);
  }

  /**
   * This is a replacement for the PageBuilder class.
   * @param pageId the pageId
   * @param flowId the flowId
   * @returns since all calls to PageBuilder called build, this will call build and return the result.
   */
  public pageBuilder(pageId: string, flowId: string): PageInstance[] {
    const workflowPage = dependencies.config.pages[pageId];

    if (!workflowPage) {
      throw new Error(`Page Id ${pageId} doesn't exit in page config.`);
    }

    return this.build(pageId, flowId, workflowPage);
  }
}

// **********************************************************************************

/**
 * This limit is a last resort to prevent unresponsive pages in the unlikely case of ending up in an infinite loop
 */
const MAX_LOOP_CYCLES = 100;

/**
 * Page specific logic and configuration store
 */
export class PageInstance extends ValidationBase {
  /**
   * Current page title
   */
  #currentPageTitle = '';

  /**
   * Page config as specified at `workflow-pages.json`
   */
  public readonly config: WorkflowPage;
  public readonly instanceId: string;

  /**
   * A reference to the next page, this property will be set when flow is generated
   */
  public nextPage: PageInstance;

  /**
   * A reference to the previous page, this property will be set when flow is generated
   */
  public previousPage: PageInstance;

  /**
   * action loading - true if the loading animation is in progress(see action-page.class)
   */
  public actionButtonIsLoading = false;

  /**
   * save/continue button to disabled state
   */
  public continueButtonDisabled = false;

  /**
   * an indicator flag to indicate if the action page logic/request has failed
   */
  public actionLogicIsFailed = false;

  /**
   * property to indicate default page type
   */
  public defaultPageType: CoreCustomPageType;

  /** flag to indicate page is dirty. Used for custom page which dont have any fields or cards */
  public dirty = false;

  /**
   * Ordered list of card objects for the page
   */
  readonly #cards: CardInstance[] = [];

  /**
   * Lock `#cards` array. This is to prevent redundant calls for the `.cards` getter to trigger nested cycles
   */
  #cardsLock = false;

  /**
   * Page field helper instance
   */
  readonly #pageHelper: PageHelper;

  /**
   * an indicator flag to indicate if the save request has failed
   */
  public saveIsFailed = false;

  /**
   * an indicator flag to indicate if the app is migrated to arc OS
   */
  public isAppMigratedToOS = false;

  /**
   * Page route helper instance
   */
  readonly #pageRouteHelper: PageRouteHelper;
  /** ReviewPageId returned from logic function */
  #reviewPageIds: string[];

  /**
   * Create or return a unique instance of a page
   * @param id config id (as set in `workflow-pages.json`)
   * @param pageIterationId config id (as set in `workflow-pages.json`)
   */
  constructor(
    private readonly cardService: CardService,
    public readonly id: string,
    private readonly originalId: string,
    public readonly flowId: string,
    public pageIterationId?: number
  ) {
    super();

    this.instanceId = `${flowId}/${this.id}`;
    const instanceConfigId = `${flowId}/${originalId}`;

    if (dependencies.config.workflowSpecificPages[instanceConfigId]) {
      this.config = { ...dependencies.config.pages[originalId], ...dependencies.config.workflowSpecificPages[instanceConfigId] };
    } else {
      this.config = dependencies.config.pages[originalId];
    }

    this.#pageHelper = new PageHelper(this);
    this.#pageRouteHelper = new PageRouteHelper(this.config);
  }

  /**
   * Whether removebutton should be visible for page
   */
  public get showRemove(): boolean {
    return this.config.showRemove && dependencies.logicService.evaluate(this.config.showRemove, { page: this }, true);
  }

  /**
   * Dynamically sized array (to support loops) with all card instances for the page
   * Will use the function referred by the .iterationMax property, or the size from array on the .array property on card config
   */
  public get cards(): CardInstance[] {
    if (!this.#cardsLock) {
      const logicService = dependencies.logicService;
      this.#cardsLock = true;
      this.#cards.length = 0;
      this.config.cards.forEach((cardId) => {
        if (cardId.indexOf(PATH_ITERATION_SYMBOL) > -1) {
          // iterated card
          let cardConfig = dependencies.config.cards[cardId];
          const instanceConfigId = `${this.flowId}/${cardId}`;
          if (dependencies.config.workflowSpecificCards[instanceConfigId]) {
            cardConfig = { ...dependencies.config.cards[cardId], ...dependencies.config.workflowSpecificCards[instanceConfigId] };
          }

          const currentSize = cardConfig.array && StatefulValue.arraySize(cardConfig.array, this.pageIterationId);
          this.cardGetterHelper(currentSize, cardConfig);
          const iterationMaxFnDesc = cardConfig.iterationMax;
          const arrayLimit = !iterationMaxFnDesc && cardConfig.array ? currentSize : null;
          const previousCards: CardInstance[] = [];
          let card: CardInstance;
          for (
            let i = 0;
            i < MAX_LOOP_CYCLES &&
            (arrayLimit != null
              ? i < arrayLimit
              : !logicService.evaluate(iterationMaxFnDesc, { page: this, iteratedCardId: cardId, previousCards, iteration: i }, true));
            i++
          ) {
            card = this.cardService.createCard(cardId, this.flowId, i, this.pageIterationId);
            previousCards.push(card);
          }
          this.#cards.push(...previousCards);
        } else {
          this.#cards.push(this.cardService.createCard(cardId, this.flowId, null, this.pageIterationId));
        }
      });
      this.#cardsLock = false;
    }
    return this.#cards;
  }

  private cardGetterHelper(currentSize, cardConfig) {
    if (cardConfig.arrayMinimum > 0 && currentSize < +cardConfig.arrayMinimum && !StatefulValue.isArrayAdjusting(cardConfig.array)) {
      StatefulValue.arrayAdd(
        this.pageIterationId != null ? cardConfig.array.replace(PATH_PAGE_ITERATION_SYMBOL, String(this.pageIterationId)) : cardConfig.array
      );
    }
  }

  /**
   * Page proxy provides helper functions for page fields and validations
   */
  public get helper(): PageHelper {
    return this.#pageHelper;
  }

  /**
   * Page helper functions for routes
   */
  public get routeHelper(): PageRouteHelper {
    return this.#pageRouteHelper;
  }

  /**
   * @inheritdoc
   */
  public hasValidation(fnName: string): PrimitiveType[] {
    return dependencies.logicService.hasFunction(fnName, this.config.validation);
  }

  /** Remove iteratable Page   */
  public remove(): void {
    if (this.config.onRemove) {
      dependencies.logicService.evaluate(this.config.onRemove, { page: this }, true);
    } else {
      StatefulValue.arrayRemove(this.config.array, this.pageIterationId);
    }
  }

  /**
   * Trigger revalidation
   */
  public revalidate(): string[] {
    if (this.visible) {
      const validation = dependencies.logicService.evaluate(this.config.validation, { page: this }, 'collect', null, this.pageIterationId);
      return this.spreadValidation(validation);
    }
    return [];
  }

  public get isRedirectPage(): boolean {
    return this.isRedirect(this.config.type);
  }

  public get isActionPage(): boolean {
    return this.isAction(this.config.type);
  }

  // TODO: Seems overly engineered to have a method to check a string.
  public isRedirect(pageType: PageType) {
    return pageType === 'redirect';
  }

  public isAction(pageType: PageType) {
    return pageType === 'action';
  }

  public get redirectUrl(): string {
    return dependencies.stringVariableService.parse(this.config.redirectUrl, this.pageIterationId) as string;
  }
  /** Flag indicates is hidden in Admin */
  /** if a page is hidden in workflow config, it may or may not be added to flow based on condition (for ex. co-app pages).
   * isHiddenInAdmin flag helps to filter pages that will 'never' added to the workflow (ie. hidden in the admin portal).
   * As per AC, we need to exclude pages hidden in the Admin portal and include co-app pages for the calculation on the
   * initial page of the flow before we know whether co-app will be added or not */
  public get isHiddenInAdmin(): boolean {
    return !!dependencies.logicService.hasFunction('never', this.config.visible);
  }

  /**
   * Whether page should be visible
   */
  public get visible(): boolean {
    return dependencies.logicService.evaluate(this.config.visible, { page: this }, true, null, this.pageIterationId);
  }

  /**
   * Whether pageModal should be visible
   */
  public get modalVisible(): boolean {
    return (
      this.config.showModal && dependencies.logicService.evaluate(this.config.showModal, { page: this }, true, null, this.pageIterationId)
    );
  }
  /**
   * Call the call back
   */
  public whenPageTitleChanged(callback: () => void): void {
    const changed = this.#currentPageTitle !== this.customTitleOrDefault;
    if (changed) {
      this.#currentPageTitle = this.customTitleOrDefault;
      callback();
    }
  }

  /**
   * on page load
   */
  public onLoad(): void {
    dependencies.logicService.evaluate(this.config.onLoad, { page: this });
  }

  /**
   * Return page path considering iterative logic
   */
  public get path(): string {
    return this.routeHelper.getPagePath(this.pageIterationId);
  }

  public get title(): string {
    return dependencies.stringVariableService.parse(this.config.title, this.pageIterationId) as string;
  }

  public get subtitle(): string {
    return dependencies.stringVariableService.parse(this.config.subtitle, this.pageIterationId) as string;
  }

  /**
   * Custom page title or default title
   */
  public get customTitleOrDefault(): string {
    return CustomTextHelper.customTextOrDefault(this.config.titleFn, { page: this }, this.title, null, this.pageIterationId);
  }

  /**
   * Custom page subtitle or default subtitle
   */
  public get customSubtitleOrDefault(): string {
    return CustomTextHelper.customTextOrDefault(this.config.subtitleFn, { page: this }, this.subtitle, null, this.pageIterationId);
  }

  /**
   * Page allows partial save to be sent to arc OS
   */
  public get allowSavePartialApp(): boolean {
    if (!this.config.allowSavePartialApp) {
      return false;
    }

    if (typeof this.config.allowSavePartialApp === 'boolean') {
      return this.config.allowSavePartialApp;
    }

    return dependencies.logicService.evaluate(this.config.allowSavePartialApp, { page: this }, true, null, this.pageIterationId);
  }

  /**
   * Page triggers an Application ID to be generated from arc OS
   */
  public get generateAppId(): boolean {
    if (!this.config.generateAppId) {
      return false;
    }

    if (typeof this.config.generateAppId === 'boolean') {
      return this.config.generateAppId;
    }

    return dependencies.logicService.evaluate(this.config.generateAppId, { page: this }, true, null, this.pageIterationId);
  }

  /**
   * Whether page should be visible
   */
  public get showNavigation(): boolean {
    // By default we want to hide navigation bar
    if (this.config.showNavigation === undefined) {
      return false;
    }

    if (typeof this.config.showNavigation === 'boolean') {
      return this.config.showNavigation;
    }

    return dependencies.logicService.evaluate(this.config.showNavigation, { page: this }, true, null, this.pageIterationId);
  }

  /**
   * Whether page navigation should be disabled
   */
  public get disableNavigation(): boolean {
    if (!this.config.disableInNavigation) {
      return false;
    }

    if (typeof this.config.disableInNavigation === 'boolean') {
      return this.config.disableInNavigation;
    }

    return dependencies.logicService.evaluate(this.config.disableInNavigation, { page: this }, true, null, this.pageIterationId);
  }

  public get hideInReview(): boolean {
    if (!this.config.hideInReview) {
      return false;
    }

    if (typeof this.config.hideInReview === 'boolean') {
      return this.config.hideInReview;
    }

    return dependencies.logicService.evaluate(this.config.hideInReview, { page: this }, true, null, this.pageIterationId);
  }

  public get hidePageNextButton(): boolean {
    if (!this.config.hidePageNextButton) {
      return false;
    }

    if (typeof this.config.hidePageNextButton === 'boolean') {
      return this.config.hidePageNextButton;
    }

    return dependencies.logicService.evaluate(this.config.hidePageNextButton, { page: this }, true, null, this.pageIterationId);
  }
  /** hide Page Previous Button */
  public get hidePagePreviousButton(): boolean {
    if (!this.config.hidePagePreviousButton) {
      return false;
    }

    if (typeof this.config.hidePagePreviousButton === 'boolean') {
      return this.config.hidePagePreviousButton;
    }

    return dependencies.logicService.evaluate(this.config.hidePagePreviousButton, { page: this }, true, null, this.pageIterationId);
  }

  public get disableAllNavigation(): boolean {
    if (!this.config.disableAllNavigation) {
      return false;
    }

    if (typeof this.config.disableAllNavigation === 'boolean') {
      return this.config.disableAllNavigation;
    }

    return dependencies.logicService.evaluate(this.config.disableAllNavigation, { page: this }, true, null, this.pageIterationId);
  }

  /** Returns reviewPageId  */
  public get reviewPageIds(): string[] {
    /** Returning value from private variable to avoid repeated excution of logic */
    if (this.#reviewPageIds) return this.#reviewPageIds;
    if (this.config.reviewPageIdsFn?.length) {
      this.#reviewPageIds = dependencies.logicService.evaluate(
        this.config.reviewPageIdsFn,
        { page: this },
        'generic',
        null,
        this.pageIterationId
      );
      return this.#reviewPageIds;
    }

    if (this.config.reviewPageIds?.length) {
      return this.config.reviewPageIds;
    }

    return [];
  }

  /** Returns Optional Review Page to be shown or not */
  public get reviewOptional(): boolean {
    return this.config.reviewOptional;
  }

  /** Application Review Cards to be hidden or not  */
  public get hideOptionalReviewPages(): boolean {
    return this.config.hideOptionalReviewPages;
  }

  /** get eligibility question toggle flag */
  public get hideEligibilityQuestions(): boolean {
    return this.config.hideEligibilityQuestions;
  }

  /**
   * Adds an optional automation ID as defined in the workflow.jsons
   * Will fall back to the field ID if no automation ID is defined
   * @returns automation Id
   */
  public get automationId(): string {
    return this.config?.automationId ? this.config.automationId : this.id;
  }

  /**
   * flag used to show/hide Edit on condensed view / review page
   */
  public get showEditOnCondensedCards(): boolean {
    if (typeof this.config?.showEditOnCondensedCards === 'boolean') return this.config?.showEditOnCondensedCards;
    return true;
  }

  public get customModalType(): string {
    if (typeof this.config.customModalType === 'string') return this.config.customModalType;

    const result = dependencies.logicService.evaluate<string>(this.config.customModalType, { page: this }, 'single');
    return Array.isArray(result) ? result[0] : result;
  }
}
